// CopyLinkButton — copy a link to exactly where you are.
//
// Alex, 2026-08-05: "it has to be very basic, simply to make it easy for me to let you
// know what you have to check, or the same thing if you want me to check something."
// Before this, a location had to be described in prose — and "XX>XX2>XP3" was read as
// product codes when they were CATEGORY codes, which cost a round trip and a wrong answer.
//
// Clipboard handling is deliberately the same three-step as DbIdBadge, for the same
// reason recorded there: panel mode runs inside Onshape's cross-origin iframe, where
// navigator.clipboard is blocked unless the host sets allow="clipboard-write". So try the
// async API, fall back to execCommand (which an iframe does allow), and if both fail SAY
// SO — a copy button that silently does nothing is how the last one was found broken.

import { useState, useRef } from 'react';
import { LINK } from '../icons.js';
import { BTN_SECONDARY } from '../theme-tokens.js';

export default function CopyLinkButton({ href, title = 'Copy a link to this view' }) {
  const [state, setState] = useState('idle'); // idle | copied | failed
  const areaRef = useRef(null);
  if (!href) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setState('copied');
      setTimeout(() => setState('idle'), 1500);
      return;
    } catch { /* blocked in the iframe — fall through */ }
    try {
      const ta = areaRef.current;
      ta.value = href;
      ta.select();
      if (!document.execCommand('copy')) throw new Error('execCommand refused');
      setState('copied');
      setTimeout(() => setState('idle'), 1500);
    } catch {
      setState('failed');
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button
        onClick={copy}
        title={`${title}\n${href}`}
        style={{ ...BTN_SECONDARY, whiteSpace: 'nowrap' }}
      >{LINK} {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : 'Link'}</button>
      {/* The last resort: if both copy paths are blocked the value is still selectable
          text, so a manual select-copy always works. Off-screen, not display:none —
          execCommand cannot select inside a hidden element. */}
      <textarea
        ref={areaRef}
        readOnly
        defaultValue={href}
        style={{ position: 'absolute', left: -9999, top: 0, width: 1, height: 1, opacity: 0 }}
        aria-hidden="true"
        tabIndex={-1}
      />
    </span>
  );
}
